/**
 * Roulette: pockets, colors, both wheel orders, the closed v1 bet registry,
 * and settlement. No cards, no hidden information, no player decisions after
 * the window closes — the whole game is "where did the ball land, what does
 * that pay", so the engine is pure functions over one pocket.
 *
 * No room, no sockets: the server layer supplies the pocket (from its queued
 * stack in tests, else `room.rng`) and the rules.
 */
import type { RouletteRules } from './rouletteRules.js';

/**
 * One pocket on the wheel: 0–36, or the American double zero. `'00'` is a
 * string because it is not a number the maths ever wants to see — it loses
 * every outside bet exactly like 0 does.
 */
export type RoulettePocket = number | '00';

export type RouletteWheel = 'european' | 'american';

/**
 * The European wheel, in physical order clockwise. The TV draws the ring in
 * this order, and `spinWheel` indexes into it, so a pocket's angular home is
 * its index here.
 */
export const EUROPEAN_WHEEL_ORDER: readonly RoulettePocket[] = [
  0, 32, 15, 19, 4, 21, 2, 25, 17, 34, 6, 27, 13, 36, 11, 30, 8, 23, 10, 5, 24,
  16, 33, 1, 20, 14, 31, 9, 22, 18, 29, 7, 28, 12, 35, 3, 26,
];

/** The American wheel: same physical order plus `'00'` opposite the zero. */
export const AMERICAN_WHEEL_ORDER: readonly RoulettePocket[] = [
  0, 28, 9, 26, 30, 11, 7, 20, 32, 17, 5, 22, 34, 15, 3, 24, 36, 13, 1, '00',
  27, 10, 25, 29, 12, 8, 19, 31, 18, 6, 21, 33, 16, 4, 23, 35, 14, 2,
];

/** The wheel a table spins, from its rules. */
export const wheelOrder = (wheel: RouletteWheel): readonly RoulettePocket[] =>
  wheel === 'american' ? AMERICAN_WHEEL_ORDER : EUROPEAN_WHEEL_ORDER;

const WHEEL_POCKETS: Record<RouletteWheel, ReadonlySet<RoulettePocket>> = {
  european: new Set(EUROPEAN_WHEEL_ORDER),
  american: new Set(AMERICAN_WHEEL_ORDER),
};

/**
 * Does this wheel actually have that pocket? The European wheel has no `'00'`,
 * so a straight-up bet on it there could never win — the table must refuse the
 * stack rather than take dead money. Set membership also rejects 37, -1 and
 * 17.5 in one check, because a pocket is exactly what the wheel order lists.
 */
export const isPocketOnWheel = (pocket: RoulettePocket, wheel: RouletteWheel): boolean =>
  WHEEL_POCKETS[wheel].has(pocket);

/** The eighteen red pockets. 0 and '00' are green and beat both colors. */
export const RED_NUMBERS: ReadonlySet<number> = new Set([
  1, 3, 5, 7, 9, 12, 14, 16, 18, 19, 21, 23, 25, 27, 30, 32, 34, 36,
]);

export type PocketColor = 'red' | 'black' | 'green';

export function pocketColor(pocket: RoulettePocket): PocketColor {
  if (pocket === '00' || pocket === 0) return 'green';
  return RED_NUMBERS.has(pocket) ? 'red' : 'black';
}

/** True when the pocket is a zero of either kind: every outside bet dies here. */
export const isZero = (pocket: RoulettePocket): pocket is 0 | '00' =>
  pocket === '00' || pocket === 0;

// --- the bet registry -------------------------------------------------------

/**
 * The menu. The outside bets, the straight-up, and the inside spreads — the
 * last of which are pure registry data: an anchor plus a set of offsets, so
 * they cost the engine nothing but a row each.
 */
export type RouletteBetId =
  | 'straight'
  | 'splitDown'
  | 'splitAcross'
  | 'street'
  | 'corner'
  | 'sixline'
  | 'red'
  | 'black'
  | 'odd'
  | 'even'
  | 'low'
  | 'high'
  | 'dozen1'
  | 'dozen2'
  | 'dozen3'
  | 'column1'
  | 'column2'
  | 'column3';

export const VALID_ROULETTE_BETS: readonly RouletteBetId[] = [
  'straight',
  'splitDown',
  'splitAcross',
  'street',
  'corner',
  'sixline',
  'red',
  'black',
  'odd',
  'even',
  'low',
  'high',
  'dozen1',
  'dozen2',
  'dozen3',
  'column1',
  'column2',
  'column3',
];

/** The straight-up target: 0–36 or '00'. Every other bet takes none. */
export type RouletteBetNum = number | '00';

export interface RouletteBetDef {
  id: RouletteBetId;
  label: string;
  odds: string;
  /** The ":1" side of the odds — chips won per chip staked. */
  multiplier: number;
  /** Whether `num` must accompany the bet, and what it can be. */
  wantsNum: boolean;
  /**
   * The legal anchors for an inside spread. Absent on the straight-up, whose
   * target is any pocket the wheel has — including the zeros, which no spread
   * ever reaches. Absent on the outside bets, which take no target at all.
   */
  anchors?: ReadonlySet<number>;
  /** Does this pocket take the bet? */
  covers(pocket: RoulettePocket, num?: RouletteBetNum): boolean;
}

const numEquals = (pocket: RoulettePocket, num: RouletteBetNum | undefined): boolean =>
  num !== undefined && pocket === num;

const dozens: Record<'dozen1' | 'dozen2' | 'dozen3', [number, number]> = {
  dozen1: [1, 12],
  dozen2: [13, 24],
  dozen3: [25, 36],
};

const columns: Record<'column1' | 'column2' | 'column3', number> = {
  // Column 1 is the left rail: 1, 4, … 34. A pocket's column is n mod 3.
  column1: 1,
  column2: 2,
  column3: 0,
};


// --- the inside spreads -----------------------------------------------------

/*
 * The layout is three rails of twelve, read across: a cell's neighbour one
 * step across the table is n+3, and one step down the rail is n+1. Every
 * inside bet is therefore an anchor — its lowest number — plus a fixed set of
 * offsets, which is why they are data and not engine work.
 *
 *     3  6  9 12 …      splitAcross: n, n+3   (neighbours across)
 *     2  5  8 11 …      splitDown:   n, n+1   (neighbours down a rail)
 *     1  4  7 10 …      street: a whole rail  corner: a 2x2  sixline: two rails
 *
 * Zero-adjacent spreads (the 0-1 split, the basket) are deliberately absent:
 * they straddle the top of the layout, differ between the two wheels, and are
 * not worth the special case at a house party.
 */
const INSIDE_OFFSETS = {
  splitDown: [0, 1],
  splitAcross: [0, 3],
  street: [0, 1, 2],
  corner: [0, 1, 3, 4],
  sixline: [0, 1, 2, 3, 4, 5],
} as const;

export type RouletteInsideBetId = keyof typeof INSIDE_OFFSETS;

const anchorsWhere = (fits: (n: number) => boolean): ReadonlySet<number> =>
  new Set(Array.from({ length: 36 }, (_, i) => i + 1).filter(fits));

/**
 * Which numbers can anchor each spread. A rail ends at a multiple of three, so
 * a `splitDown` cannot start on one; a corner needs a number to its right and
 * below it; a six line needs a whole rail after its own.
 */
const INSIDE_ANCHORS: Record<RouletteInsideBetId, ReadonlySet<number>> = {
  splitDown: anchorsWhere((n) => n % 3 !== 0),
  splitAcross: anchorsWhere((n) => n <= 33),
  street: anchorsWhere((n) => n % 3 === 1),
  corner: anchorsWhere((n) => n % 3 !== 0 && n <= 32),
  sixline: anchorsWhere((n) => n % 3 === 1 && n <= 31),
};

/** The numbers one spread covers, lowest first. Empty for a bad anchor. */
export function insideNumbers(id: RouletteInsideBetId, anchor: number): number[] {
  if (!INSIDE_ANCHORS[id].has(anchor)) return [];
  return INSIDE_OFFSETS[id].map((offset) => anchor + offset);
}

/** Every anchor a spread accepts, ascending — the UI enumerates from this. */
export const insideAnchors = (id: RouletteInsideBetId): number[] =>
  [...INSIDE_ANCHORS[id]].sort((a, b) => a - b);

const insideDef = (
  id: RouletteInsideBetId,
  label: string,
  multiplier: number,
): RouletteBetDef => ({
  id,
  label,
  odds: `${multiplier}:1`,
  multiplier,
  wantsNum: true,
  anchors: INSIDE_ANCHORS[id],
  // A zero is never on a spread, and `insideNumbers` already refuses a bad
  // anchor, so an unplaceable stack can never be paid by a coincidence.
  covers: (pocket, num) =>
    typeof pocket === 'number' &&
    typeof num === 'number' &&
    insideNumbers(id, num).includes(pocket),
});

export const ROULETTE_BETS: Record<RouletteBetId, RouletteBetDef> = {
  straight: {
    id: 'straight',
    label: 'Straight up',
    odds: '35:1',
    multiplier: 35,
    wantsNum: true,
    covers: (pocket, num) => numEquals(pocket, num),
  },
  splitDown: insideDef('splitDown', 'Split', 17),
  splitAcross: insideDef('splitAcross', 'Split', 17),
  street: insideDef('street', 'Street', 11),
  corner: insideDef('corner', 'Corner', 8),
  sixline: insideDef('sixline', 'Six line', 5),
  red: {
    id: 'red',
    label: 'Red',
    odds: '1:1',
    multiplier: 1,
    wantsNum: false,
    covers: (pocket) => pocketColor(pocket) === 'red',
  },
  black: {
    id: 'black',
    label: 'Black',
    odds: '1:1',
    multiplier: 1,
    wantsNum: false,
    covers: (pocket) => pocketColor(pocket) === 'black',
  },
  odd: {
    id: 'odd',
    label: 'Odd',
    odds: '1:1',
    multiplier: 1,
    wantsNum: false,
    covers: (pocket) => !isZero(pocket) && pocket % 2 === 1,
  },
  even: {
    id: 'even',
    label: 'Even',
    odds: '1:1',
    multiplier: 1,
    wantsNum: false,
    covers: (pocket) => !isZero(pocket) && pocket % 2 === 0,
  },
  low: {
    id: 'low',
    label: 'Low 1–18',
    odds: '1:1',
    multiplier: 1,
    wantsNum: false,
    covers: (pocket) => !isZero(pocket) && pocket >= 1 && pocket <= 18,
  },
  high: {
    id: 'high',
    label: 'High 19–36',
    odds: '1:1',
    multiplier: 1,
    wantsNum: false,
    covers: (pocket) => !isZero(pocket) && pocket >= 19 && pocket <= 36,
  },
  dozen1: {
    id: 'dozen1',
    label: '1st 12',
    odds: '2:1',
    multiplier: 2,
    wantsNum: false,
    covers: (pocket) => !isZero(pocket) && inRange(pocket, dozens.dozen1),
  },
  dozen2: {
    id: 'dozen2',
    label: '2nd 12',
    odds: '2:1',
    multiplier: 2,
    wantsNum: false,
    covers: (pocket) => !isZero(pocket) && inRange(pocket, dozens.dozen2),
  },
  dozen3: {
    id: 'dozen3',
    label: '3rd 12',
    odds: '2:1',
    multiplier: 2,
    wantsNum: false,
    covers: (pocket) => !isZero(pocket) && inRange(pocket, dozens.dozen3),
  },
  column1: {
    id: 'column1',
    label: 'Column 1',
    odds: '2:1',
    multiplier: 2,
    wantsNum: false,
    covers: (pocket) => !isZero(pocket) && pocket % 3 === columns.column1,
  },
  column2: {
    id: 'column2',
    label: 'Column 2',
    odds: '2:1',
    multiplier: 2,
    wantsNum: false,
    covers: (pocket) => !isZero(pocket) && pocket % 3 === columns.column2,
  },
  column3: {
    id: 'column3',
    label: 'Column 3',
    odds: '2:1',
    multiplier: 2,
    wantsNum: false,
    covers: (pocket) => !isZero(pocket) && pocket % 3 === columns.column3,
  },
};

const inRange = (pocket: number, [lo, hi]: [number, number]): boolean =>
  pocket >= lo && pocket <= hi;

/** Is this bet paid even money — the only kind `laPartage` ever rescues? */
export const isEvenMoneyBet = (id: RouletteBetId): boolean =>
  ROULETTE_BETS[id].multiplier === 1;

/**
 * A straight-up bet needs a target, and only a pocket this wheel has will do.
 * An inside spread needs an anchor its shape actually fits on the layout —
 * 36 cannot start a split down its rail, 33 cannot start a corner. Every
 * outside bet must carry no target at all.
 */
export function validBetNum(
  id: RouletteBetId,
  num: RouletteBetNum | undefined,
  wheel: RouletteWheel,
): boolean {
  const def = ROULETTE_BETS[id];
  if (!def.wantsNum) return num === undefined;
  if (def.anchors) return typeof num === 'number' && def.anchors.has(num);
  return num !== undefined && isPocketOnWheel(num, wheel);
}

/**
 * Every pocket this stack takes, in wheel order — what the layout lights up
 * when a spread is selected, and the honest answer to "what am I covering".
 */
export function coveredPockets(
  id: RouletteBetId,
  num: RouletteBetNum | undefined,
  wheel: RouletteWheel,
): RoulettePocket[] {
  const def = ROULETTE_BETS[id];
  return wheelOrder(wheel).filter((pocket) => def.covers(pocket, num));
}

/** Is this one of the inside spreads — a shape anchored on the layout? */
export const isInsideBet = (id: RouletteBetId): id is RouletteInsideBetId =>
  Object.hasOwn(INSIDE_OFFSETS, id);

/**
 * How a stack reads on a phone or a TV: "Split 5/8", "Street 4–6",
 * "Straight 17", "Red". Ranges for the shapes that run in a line, a slash
 * list for the ones that do not.
 */
export function describeRouletteBet(
  id: RouletteBetId,
  num: RouletteBetNum | undefined,
): string {
  const def = ROULETTE_BETS[id];
  if (id === 'straight') return num === undefined ? def.label : `${def.label} ${num}`;
  if (!isInsideBet(id) || typeof num !== 'number') return def.label;
  const nums = insideNumbers(id, num);
  if (nums.length === 0) return def.label;
  if (id === 'street' || id === 'sixline') {
    return `${def.label} ${nums[0]}–${nums[nums.length - 1]}`;
  }
  return `${def.label} ${nums.join('/')}`;
}

export interface RouletteBetSpec {
  id: RouletteBetId;
  num?: RouletteBetNum;
  amount: number;
}

/**
 * One pocket's landing. `spinWheel` draws a wheel index from the room's rng —
 * the same seam the shoe builds go through, so a seeded rng replays a session.
 */
export function spinWheel(rules: RouletteRules, rng: (max: number) => number): RoulettePocket {
  const order = wheelOrder(rules.wheel);
  return order[rng(order.length)]!;
}

/**
 * Chips returned to one bet, stake included. A loss pays 0 — except an
 * even-money bet that dies on a zero under `laPartage`, which gets half its
 * stake back, floored to whole chips. The half-refund is the only fractional
 * number roulette can produce, and chips are integers.
 */
export function settleRouletteBet(
  bet: RouletteBetSpec,
  pocket: RoulettePocket,
  rules: RouletteRules,
): number {
  const def = ROULETTE_BETS[bet.id];
  if (def.covers(pocket, bet.num)) return bet.amount + bet.amount * def.multiplier;
  if (rules.laPartage && isZero(pocket) && isEvenMoneyBet(bet.id)) {
    return Math.floor(bet.amount / 2);
  }
  return 0;
}

/** Per-bet payouts, in the order the bets arrive. */
export function settleRouletteBets(
  bets: readonly RouletteBetSpec[],
  pocket: RoulettePocket,
  rules: RouletteRules,
): number[] {
  return bets.map((bet) => settleRouletteBet(bet, pocket, rules));
}
